import L from 'leaflet'
import { addBasemap } from '@/helpers/basemap'
import type { Neighborhood, Subgeo } from '../types'
import type { ColorPair } from './color-utils'

export interface NeighborhoodData {
	name: string
	stats: unknown
}

export interface GeoJSONFeatureProperties {
	id: string
	name: string
}

export interface MapEventHandlers {
	onNeighborhoodClick: (id: string) => void
	onNeighborhoodContextMenu: (
		id: string,
		name: string,
		x: number,
		y: number
	) => void
	onTooltipRequest: (id: string) => HTMLDivElement | null
}

const isValidCoordinate = (coord: unknown): coord is [number, number] => {
	return (
		Array.isArray(coord) &&
		coord.length >= 2 &&
		typeof coord[0] === 'number' &&
		typeof coord[1] === 'number'
	)
}

/**
 * Validates GeoJSON geometry structure and coordinates.
 */
export const validateGeometry = (geom: unknown): boolean => {
	if (!geom || typeof geom !== 'object') {
		return false
	}
	const g = geom as { type: string; coordinates: unknown }
	if (!Array.isArray(g.coordinates)) {
		return false
	}

	if (g.type === 'Polygon') {
		const c = g.coordinates
		return (
			Array.isArray(c) &&
			Array.isArray(c[0]) &&
			c[0].length > 0 &&
			isValidCoordinate(c[0][0])
		)
	}
	if (g.type === 'MultiPolygon') {
		const c = g.coordinates
		return (
			Array.isArray(c) &&
			Array.isArray(c[0]) &&
			Array.isArray(c[0][0]) &&
			c[0][0].length > 0 &&
			isValidCoordinate(c[0][0][0])
		)
	}
	return false
}

/**
 * Filters neighborhoods with valid geometries.
 */
export const filterValidNeighborhoods = (
	neighborhoods: Neighborhood[]
): Neighborhood[] => {
	return neighborhoods.filter((n) => validateGeometry(n?.dim_geom))
}

/**
 * Creates O(1) lookup map for neighborhood data.
 */
export const createNeighborhoodDataMap = (
	neighborhoods: Neighborhood[]
): Map<string, NeighborhoodData> => {
	const map = new Map<string, NeighborhoodData>()
	const len = neighborhoods.length
	for (let i = 0; i < len; i++) {
		const n = neighborhoods[i]
		map.set(n.acp_id, {
			name: n.place_name,
			stats: (n.dim_geom as { properties?: unknown }).properties
		})
	}
	return map
}

/**
 * Creates O(1) lookup map for neighborhood to subgeo assignments.
 */
export const createSubgeoLookupMap = (
	subgeos: Subgeo[]
): Map<string, Subgeo> => {
	const map = new Map<string, Subgeo>()
	const len = subgeos.length
	for (let i = 0; i < len; i++) {
		const subgeo = subgeos[i]
		const neighborhoodIds = subgeo.neighborhoodIds
		const nLen = neighborhoodIds.length
		for (let j = 0; j < nLen; j++) {
			map.set(neighborhoodIds[j], subgeo)
		}
	}
	return map
}

/**
 * Calculates the area of a polygon ring using the Shoelace formula.
 * Used for sorting neighborhoods by size to ensure smaller overlapping
 * neighborhoods render on top and remain clickable.
 */
const calculateRingArea = (coords: number[][]): number => {
	let area = 0
	const len = coords.length

	for (let i = 0; i < len - 1; i++) {
		const [x1, y1] = coords[i]
		const [x2, y2] = coords[i + 1]
		area += x1 * y2 - x2 * y1
	}

	return area / 2
}

/**
 * Calculates total area for GeoJSON geometry (Polygon or MultiPolygon).
 * Returns approximate area based on coordinate space (not true geographic area).
 * Sufficient for sorting by relative size.
 */
const calculatePolygonArea = (geometry: GeoJSON.Geometry): number => {
	if (geometry.type === 'Polygon') {
		return Math.abs(calculateRingArea(geometry.coordinates[0]))
	}
	if (geometry.type === 'MultiPolygon') {
		return geometry.coordinates.reduce((total, polygon) => {
			return total + Math.abs(calculateRingArea(polygon[0]))
		}, 0)
	}
	return 0
}

/**
 * Builds GeoJSON FeatureCollection from neighborhoods.
 * Neighborhoods are sorted by area (largest first) to ensure smaller
 * neighborhoods render on top when geometries overlap, keeping them clickable.
 */
export const buildGeoJSONFeatureCollection = (
	neighborhoods: Neighborhood[]
): GeoJSON.FeatureCollection<
	GeoJSON.Geometry,
	GeoJSONFeatureProperties
> | null => {
	if (neighborhoods.length === 0) {
		return null
	}

	const neighborhoodsWithArea = neighborhoods.map((n) => ({
		neighborhood: n,
		area: calculatePolygonArea(n.dim_geom)
	}))

	neighborhoodsWithArea.sort((a, b) => b.area - a.area)

	const features = neighborhoodsWithArea.map(({ neighborhood: n }) => ({
		type: 'Feature' as const,
		properties: {
			id: n.acp_id,
			name: n.place_name
		},
		geometry: n.dim_geom
	}))

	return {
		type: 'FeatureCollection',
		features
	}
}

/**
 * Creates Leaflet style function with color mapping.
 */
export const createStyleFunction = (
	selectedIds: Set<string>,
	getNeighborhoodColor: (id: string, isSelected: boolean) => ColorPair
) => {
	return (feature: GeoJSON.Feature | undefined): L.PathOptions => {
		const id = feature?.properties?.id as string
		if (!id) {
			return {}
		}

		const isSelected = selectedIds.has(id)
		const colors = getNeighborhoodColor(id, isSelected)

		return {
			fillColor: colors.fill,
			fillOpacity: colors.fillOpacity,
			color: colors.border,
			weight: 1
		}
	}
}

let tooltipDebounceTimer: ReturnType<typeof setTimeout> | null = null
let currentTooltipLayer: (L.Path & { feature?: GeoJSON.Feature }) | null = null
const TOOLTIP_DELAY = 150

/**
 * Attaches mouseover handler with debounced tooltip creation.
 */
export const attachMouseOverHandler = (
	layer: L.GeoJSON,
	handlers: MapEventHandlers
): void => {
	layer.on('mouseover', (e: L.LeafletMouseEvent) => {
		const target = e.layer as L.Path & { feature?: GeoJSON.Feature }
		const neighborhoodId = target.feature?.properties?.id as string

		if (!neighborhoodId) {
			return
		}

		if (currentTooltipLayer && currentTooltipLayer !== target) {
			currentTooltipLayer.closeTooltip()
			currentTooltipLayer.unbindTooltip()
			currentTooltipLayer = null
		}

		const element = target.getElement()
		if (element) {
			;(element as HTMLElement).style.cursor = 'pointer'
		}

		if (tooltipDebounceTimer) {
			clearTimeout(tooltipDebounceTimer)
		}

		tooltipDebounceTimer = setTimeout(() => {
			const tooltipElement = handlers.onTooltipRequest(neighborhoodId)
			if (tooltipElement) {
				const tooltip = L.tooltip({
					permanent: false,
					direction: 'top',
					className: 'neighborhood-tooltip-leaflet',
					opacity: 1,
					interactive: true
				})
				tooltip.setContent(tooltipElement)
				target.bindTooltip(tooltip)
				target.openTooltip()
				currentTooltipLayer = target
			}
		}, TOOLTIP_DELAY)
	})
}

/**
 * Attaches mouseout handler to clean up cursor state.
 */
export const attachMouseOutHandler = (layer: L.GeoJSON): void => {
	layer.on('mouseout', (e: L.LeafletMouseEvent) => {
		if (tooltipDebounceTimer) {
			clearTimeout(tooltipDebounceTimer)
			tooltipDebounceTimer = null
		}

		const target = e.layer as L.Path
		const element = target.getElement()
		if (element) {
			;(element as HTMLElement).style.cursor = ''
		}
	})
}

/**
 * Attaches click handler for neighborhood selection.
 */
export const attachClickHandler = (
	layer: L.GeoJSON,
	handlers: MapEventHandlers
): void => {
	layer.on('click', (e: L.LeafletMouseEvent) => {
		L.DomEvent.stopPropagation(e)
		const target = e.layer as L.Path & { feature?: GeoJSON.Feature }
		const neighborhoodId = target.feature?.properties?.id as string
		if (neighborhoodId) {
			handlers.onNeighborhoodClick(neighborhoodId)
		}
	})
}

/**
 * Attaches context menu handler for right-click actions.
 */
export const attachContextMenuHandler = (
	layer: L.GeoJSON,
	handlers: MapEventHandlers,
	editingSubgeoId: string | null
): void => {
	layer.on('contextmenu', (e: L.LeafletMouseEvent) => {
		if (editingSubgeoId) {
			return
		}

		L.DomEvent.stopPropagation(e)
		L.DomEvent.preventDefault(e.originalEvent)

		const target = e.layer as L.Path & { feature?: GeoJSON.Feature }
		const neighborhoodId = target.feature?.properties?.id as string
		const neighborhoodName = target.feature?.properties?.name as string

		if (neighborhoodId && neighborhoodName) {
			handlers.onNeighborhoodContextMenu(
				neighborhoodId,
				neighborhoodName,
				e.originalEvent.clientX,
				e.originalEvent.clientY
			)
		}
	})
}

/**
 * Attaches all event handlers to the layer (event delegation pattern).
 */
export const attachAllEventHandlers = (
	layer: L.GeoJSON,
	handlers: MapEventHandlers,
	editingSubgeoId: string | null
): void => {
	attachMouseOverHandler(layer, handlers)
	attachMouseOutHandler(layer)
	attachClickHandler(layer, handlers)
	attachContextMenuHandler(layer, handlers, editingSubgeoId)
}

/**
 * Creates GeoJSON layer with styling and event handlers.
 */
export const createGeoJSONLayer = (
	featureCollection: GeoJSON.FeatureCollection,
	styleFunction: (feature: GeoJSON.Feature | undefined) => L.PathOptions,
	handlers: MapEventHandlers,
	editingSubgeoId: string | null
): L.GeoJSON => {
	const layer = L.geoJSON(featureCollection, {
		style: styleFunction
	})

	attachAllEventHandlers(layer, handlers, editingSubgeoId)

	return layer
}

const updateBatchQueue: Array<{
	layer: L.Path
	id: string
	getStyle: (id: string) => L.PathOptions
}> = []
let updateFrameScheduled = false

const processBatchUpdate = () => {
	updateFrameScheduled = false
	const batch = updateBatchQueue.splice(0)

	for (const { layer, id, getStyle } of batch) {
		const newStyle = getStyle(id)
		layer.setStyle(newStyle)
	}
}

/**
 * Updates all layer styles in a batched requestAnimationFrame.
 */
export const updateLayerStyles = (
	layer: L.GeoJSON,
	getStyleForId: (id: string) => L.PathOptions
): void => {
	layer.eachLayer((leafletLayer) => {
		const pathLayer = leafletLayer as L.Path & { feature?: GeoJSON.Feature }
		const id = pathLayer.feature?.properties?.id as string
		if (id) {
			updateBatchQueue.push({
				layer: pathLayer,
				id,
				getStyle: getStyleForId
			})
		}
	})

	if (!updateFrameScheduled && updateBatchQueue.length > 0) {
		updateFrameScheduled = true
		requestAnimationFrame(processBatchUpdate)
	}
}

/**
 * Fits map to layer bounds with proper padding.
 */
export const fitMapToBounds = (
	map: L.Map,
	layer: L.GeoJSON,
	options?: {
		paddingTopLeft?: [number, number]
		paddingBottomRight?: [number, number]
		maxZoom?: number
		animate?: boolean
		duration?: number
	}
): void => {
	const bounds = layer.getBounds()
	if (!bounds.isValid()) {
		return
	}

	requestAnimationFrame(() => {
		map.fitBounds(bounds, {
			paddingTopLeft: options?.paddingTopLeft ?? [450, 20],
			paddingBottomRight: options?.paddingBottomRight ?? [20, 20],
			maxZoom: options?.maxZoom ?? 12,
			animate: options?.animate ?? true,
			duration: options?.duration ?? 1.5
		})
	})
}

/**
 * Zooms to a specific neighborhood by ID.
 */
export const zoomToNeighborhood = (
	map: L.Map,
	layer: L.GeoJSON,
	neighborhoodId: string,
	options?: {
		paddingTopLeft?: [number, number]
		paddingBottomRight?: [number, number]
		maxZoom?: number
		animate?: boolean
		duration?: number
	}
): { bounds: L.LatLngBounds; center: L.LatLng } | null => {
	let targetLayer: L.Layer | null = null

	layer.eachLayer((leafletLayer) => {
		const pathLayer = leafletLayer as L.Path & { feature?: GeoJSON.Feature }
		const id = pathLayer.feature?.properties?.id as string
		if (id === neighborhoodId) {
			targetLayer = pathLayer
		}
	})

	if (!targetLayer) {
		return null
	}

	const geoJsonLayer = targetLayer as L.Layer & {
		getBounds?: () => L.LatLngBounds
	}
	if (!geoJsonLayer.getBounds) {
		return null
	}

	const bounds = geoJsonLayer.getBounds()
	if (!bounds.isValid()) {
		return null
	}

	const center = bounds.getCenter()

	requestAnimationFrame(() => {
		map.fitBounds(bounds, {
			paddingTopLeft: options?.paddingTopLeft ?? [450, 20],
			paddingBottomRight: options?.paddingBottomRight ?? [20, 20],
			maxZoom: options?.maxZoom ?? 12,
			animate: options?.animate ?? true,
			duration: options?.duration ?? 1.5
		})
	})

	return { bounds, center }
}

/**
 * Initializes Leaflet map with optimized canvas renderer.
 */
export const initializeLeafletMap = (
	container: HTMLDivElement,
	options?: {
		center?: [number, number]
		zoom?: number
		minZoom?: number
		canvasTolerance?: number
		canvasPadding?: number
	}
): L.Map => {
	const map = L.map(container, {
		center: options?.center ?? [20, 0],
		zoom: options?.zoom ?? 3,
		minZoom: options?.minZoom ?? 3,
		scrollWheelZoom: true,
		preferCanvas: true,
		zoomControl: false,
		renderer: L.canvas({
			tolerance: options?.canvasTolerance ?? 5,
			padding: options?.canvasPadding ?? 0.5
		})
	})

	addBasemap(map)

	L.control
		.zoom({
			position: 'bottomright'
		})
		.addTo(map)

	return map
}
